/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {Entity} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import {PartialSuccessType} from '@ion/dls/src/types/types';
import {formatErrorMessage, partialStatusMessageFormatter} from '@ion/dls/src/utils/ComponentUtils';

/**
 * Types that could be used on user prompt notifications to potentially change colors,
 * icons, etc.
 */
export enum UserPromptTypes {
  Error = 'error',
  Warning = 'warning',
  Success = 'success'
};

/**
 * Base options for error user prompts
 */
export const errorOptions = {
  type        : UserPromptTypes.Error,
  message     : 'common:errors.generic',
  actionLabel : 'common:dismiss',
  display     : true,
  autoHide    : false
};

/**
 * Base options for success user prompts
 */
export const successOptions = {
  type                : UserPromptTypes.Success,
  message             : 'notifications:generic.success',
  actionLabel         : 'common:ok',
  display             : true,
  autoHide            : true,
  partialSuccessState : null
};

/**
 * Results for the successful add asset user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(successAddOptions(Entity.role)));
 *
 * ```
 */
export const successAddOptions = (entity: Entity) => ({
  ...successOptions,
  message         : 'common:editors.shared.successCreated',
  translateOption : {entity: capitalizeFirstLetter(entity)}
});

/**
 * Results for the error add asset user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(errorAddOptions(Entity.role)));
 *
 * ```
 */
export const errorAddOptions = (entity: Entity, err?: ServiceError) => ({
  ...errorOptions,
  message         : !isNil(err) ? formatErrorMessage(err) : 'common:editors.shared.errorCreated',
  translateOption : {entity: capitalizeFirstLetter(entity)}
});

/**
 * Results for the successful edit asset user prompt that extend the default options.
 */
export const successEditOptions = (entity: Entity, partialSuccessState: PartialSuccessType = null) => ({
  ...successAddOptions(entity),
  message             : 'common:editors.shared.successEdited',
  partialSuccessState : partialSuccessState
    ? partialStatusMessageFormatter(partialSuccessState?.results, partialSuccessState?.items) : null
});

/**
 * Results for the error add asset user prompt that extend the default options.
 */
export const errorEditOptions = (entity: Entity, err?: ServiceError) => ({
  ...errorAddOptions(entity, err),
  message : !isNil(err) ? formatErrorMessage(err) : 'common:editors.shared.errorEdited'
});
